import { useCallback, useEffect, useState } from "react";
import { api, extractAxiosError } from "@/lib/api";
import { AdminPanelShell } from "@/components/admin/AdminPanelShell";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

const ACTION_LABELS = {
    DELETE_USER: "User deleted",
    USER_DELETED_CASCADE: "User deleted (cascade)",
    CREATE_USER: "User created",
    RESET_PASSWORD: "Password reset",
    DELETE_ORG: "Organization deleted",
    DELETE_POOL: "Pool removed",
    REMOVE_ORG_MEMBER: "Org member removed",
    TRANSFER_ORG_OWNERSHIP: "Ownership transferred",
    UPDATE_ROLE: "Role updated",
    VIEW_ALL_USERS: "Viewed all users",
    VIEW_ALL_ORGS: "Viewed all organizations",
    VIEW_ORG_MEMBERS: "Viewed org members",
    VIEW_ORG_DETAIL: "Viewed org detail",
    VIEW_AUDIT_LOGS: "Viewed audit logs",
    VIEW_USER_PROFILE: "Viewed user profile",
    VIEW_POOL_PROFILE: "Viewed pool profile",
    VIEW_USER_DELETION_IMPACT: "Viewed user deletion impact",
    VIEW_PLATFORM_OVERVIEW: "Viewed platform overview",
    VIEW_TRAFFIC_ANALYTICS: "Viewed traffic analytics",
    VIEW_EMAIL_LOGS: "Viewed email logs",
    VIEW_EMAIL_LOG: "Viewed email log",
    SEND_ADMIN_EMAIL: "Sent admin email",
    EMAIL_TEST: "Sent test email",
    VIEW_DB_COLLECTIONS: "Viewed DB collections",
    VIEW_DB_DOCUMENTS: "Viewed DB documents",
    VIEW_DB_DOCUMENT: "Viewed DB document",
    UPDATE_DB_DOCUMENT: "Updated DB document",
    VIEW_ALL_POOLS: "Viewed all pools",
    ACCESS_DENIED: "Access denied",
};

function actionColor(action, success) {
    if (action === "ACCESS_DENIED" || success === false) return "text-rose-400";
    if (action?.startsWith("DELETE")) return "text-rose-300";
    if (action === "UPDATE_ROLE") return "text-amber-300";
    if (action?.startsWith("VIEW")) return "text-white/55";
    return "admin-accent-text";
}

function relTime(iso) {
    try {
        return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
    } catch (_e) { return iso; }
}

export function AdminAuditLogsPage() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ actor_email: "", action: "", date_from: "", date_to: "" });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (filters.actor_email.trim()) params.actor_email = filters.actor_email.trim();
            if (filters.action) params.action = filters.action;
            if (filters.date_from) params.date_from = new Date(filters.date_from).toISOString();
            if (filters.date_to) {
                const d = new Date(filters.date_to);
                d.setHours(23, 59, 59, 999);
                params.date_to = d.toISOString();
            }
            const { data } = await api.get("/admin/audit-logs", { params });
            setLogs(data.logs || []);
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to load audit logs"));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4">
            <div className="surface p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 min-h-[108px]">
                <div>
                    <label className="label-eyebrow block mb-1.5">Actor email</label>
                    <input
                        type="text"
                        value={filters.actor_email}
                        onChange={(e) => setFilters((f) => ({ ...f, actor_email: e.target.value }))}
                        placeholder="Filter by email"
                        className="input-dark w-full rounded-md px-3 py-2 text-sm"
                    />
                </div>
                <div>
                    <label className="label-eyebrow block mb-1.5">Action</label>
                    <select
                        value={filters.action}
                        onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
                        className="input-dark w-full rounded-md px-3 py-2 text-sm"
                    >
                        <option value="">All actions</option>
                        {Object.keys(ACTION_LABELS).map((a) => (
                            <option key={a} value={a}>{ACTION_LABELS[a]}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="label-eyebrow block mb-1.5">From date</label>
                    <input
                        type="date"
                        value={filters.date_from}
                        onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                        className="input-dark w-full rounded-md px-3 py-2 text-sm"
                    />
                </div>
                <div>
                    <label className="label-eyebrow block mb-1.5">To date</label>
                    <input
                        type="date"
                        value={filters.date_to}
                        onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                        className="input-dark w-full rounded-md px-3 py-2 text-sm"
                    />
                </div>
            </div>

            <AdminPanelShell
                title="Audit Logs"
                count={logs.length}
                loading={loading}
                empty={!loading && logs.length === 0}
                emptyMessage="No audit entries match your filters."
                variant="list"
                testId="admin-audit-panel"
                headerExtra={
                    <button onClick={load} className="btn-ghost rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1.5" data-testid="audit-refresh">
                        <RefreshCw className="h-3.5 w-3.5" /> Refresh
                    </button>
                }
            >
                <ul className="divide-y divide-white/[0.04]">
                    {logs.map((l) => {
                        const success = l.metadata?.success !== false;
                        return (
                            <li key={l.id} className="px-4 sm:px-5 py-3 flex items-start gap-3 hover:bg-white/[0.015] transition-colors min-h-[72px]" data-testid={`admin-audit-${l.id}`}>
                                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${success ? "admin-accent-dot" : "bg-rose-500/70"}`} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`text-sm font-medium ${actionColor(l.action, success)}`}>
                                            {ACTION_LABELS[l.action] || l.action}
                                        </span>
                                        {!success && <span className="text-[10px] uppercase tracking-wider text-rose-400/80 font-semibold">failed</span>}
                                    </div>
                                    <p className="text-xs text-white/50 mt-0.5">
                                        {l.actor_email || "unknown"}
                                        {l.target_type && (
                                            <>
                                                <span className="text-white/25 mx-1.5">→</span>
                                                <span className="font-mono text-white/40">{l.target_type}{l.target_id ? `:${l.target_id.slice(0, 8)}…` : ""}</span>
                                            </>
                                        )}
                                    </p>
                                    {l.metadata && Object.keys(l.metadata).filter((k) => k !== "success").length > 0 && (
                                        <p className="text-[11px] text-white/30 font-mono mt-0.5 truncate">
                                            {Object.entries(l.metadata)
                                                .filter(([k]) => k !== "success")
                                                .slice(0, 4)
                                                .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v).slice(0, 30) : v}`)
                                                .join(" · ")}
                                        </p>
                                    )}
                                    {l.ip && <p className="text-[10px] text-white/25 mt-0.5 font-mono">IP {l.ip}</p>}
                                </div>
                                <span className="text-[11px] text-white/35 font-mono shrink-0">{relTime(l.timestamp)}</span>
                            </li>
                        );
                    })}
                </ul>
            </AdminPanelShell>
        </div>
    );
}
